<template>
  <div :class="[evEvent.dark_text ? 'dark' : '', evTypecode.toLowerCase(), 'event-card']" :style="cardStyles">
    <div v-if="evTypecode !== 'DISCOVERY'" class="circle circle-main" />
    <div v-if="evTypecode !== 'DISCOVERY'" class="circle circle-date" />

    <div class="dates">
      <p>{{ dates }}</p>
    </div>

    <div class="card-content">
      <p class="title">{{ evEvent.title }}</p>
      <p class="instructor">{{ facultyTitle }}: {{ evEvent.faculty_name }}</p>
      <div class="line" />
      <p class="description">{{ evEvent.card_description }}</p>
    </div>

    <div class="buttons">
      <RegisterOrVideoButton :evEventOrCourse="evEvent" />
      <MoreInfoButton :evEventOrCourse="evEvent" @click="moreInfo" />
    </div>

    <MoreInfoPopup v-if="showMoreInfo" :evEvent="evEvent" @close="showMoreInfo = false" />
  </div>
</template>

<script>
import MoreInfoButton from '@/components/events/MoreInfoButton.vue';
import MoreInfoPopup from '@/components/events/MoreInfoPopup.vue';
import RegisterOrVideoButton from '@/components/events/RegisterOrVideoButton.vue';

export default {
  data() {
    return {
      // The type of program events
      programEventTypeCode: 'PROFESSIONAL',

      // Show popup for events are not program event
      showMoreInfo: false,
    };
  },

  props: {
    evEvent: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },

  computed: {
    evTypecode() {
      return this.evEvent.type_code;
    },
    isProgram() {
      return this.evTypecode === 'PROFESSIONAL';
    },
    isGuest() {
      return this.evTypecode === 'GUEST-EXPERT';
    },
    isPast() {
      return this.evEvent.start_date < new Date();
    },
    registerable() {
      return !this.isPast;
    },
    hasVideo() {
      return this.evEvent.video_link !== null;
    },
    playVideo() {
      return this.isPast && this.hasVideo;
    },
    cardStyles() {
      return {
        backgroundImage: `url(${this.evEvent.card_image})`,
      };
    },
    facultyTitle() {
      if (this.isProgram) {
        return 'Instructor';
      }
      if (this.isGuest) {
        return 'Guest Speaker';
      }

      return 'HUB Facilitator';
    },
    dates() {
      if (!this.evEvent.start_date || !this.evEvent.end_date) return 'Coming Soon';
      if (this.isPast) return 'Past Event';

      if (this.sameday(this.evEvent.start_date, this.evEvent.end_date)) {
        const day = this.evEvent.start_date.toLocaleString('en-CA', { dateStyle: 'long' });
        const start = this.evEvent.start_date.toLocaleString('en-CA', { hour: 'numeric', minute: 'numeric' });
        const end = this.evEvent.end_date.toLocaleString('en-CA', { hour: 'numeric', minute: 'numeric' });
        return `${day}\n${start} - ${end}`;
      }

      const start = this.evEvent.start_date.toLocaleString('en-CA', { dateStyle: 'long' });
      const end = this.evEvent.end_date.toLocaleString('en-CA', { dateStyle: 'long' });
      return `${start}\n${end}`;
    },
  },

  components: {
    RegisterOrVideoButton,
    MoreInfoButton,
    MoreInfoPopup,
  },

  methods: {
    sameday(firstDate, secondDate) {
      if (
        firstDate.getDate() === secondDate.getDate() &&
        firstDate.getDay() === secondDate.getDay() &&
        firstDate.getFullYear() &&
        secondDate.getFullYear()
      ) {
        return true;
      }

      return false;
    },
    moreInfo() {
      this.showMoreInfo = true;
    },
  },
};
</script>

<style scoped>
.event-card {
  display: inline-block;

  position: relative;
  width: 48%;
  /* min-width: 485px; */

  border-radius: 11px;
  background: black;
  background-size: cover;

  font-family: Helvetica;
  font-size: 0.94em;
  color: #ffffff;

  overflow: hidden;
}

@media (min-width: 1440px) {
  .event-card {
    font-size: 0.85em;
  }
}

.event-card.discovery.dark {
  color: #000000;
}

.event-card::after {
  display: block;
  content: '';
  padding-bottom: 56%;
}

.circle {
  position: absolute;

  border-radius: 50%;
  filter: blur(15px);
  opacity: 0.9;
  background-image: linear-gradient(262deg, #0ea1af 13%, #075b9f 100%);
}

.circle::after {
  display: block;
  content: '';
  padding-bottom: 100%;
}

.circle-main {
  top: -30%;
  left: -15%;
  width: 84%;
}

.guest-expert .circle-main {
  background-image: linear-gradient(270deg, rgba(38, 6, 64, 0.69) 9%, #0c041c 74%);
}

.circle-date {
  bottom: -21%;
  right: -3%;
  width: 40%;
}

.card-content {
  position: absolute;
  top: 10px;
  left: 16px;
}

.description {
  display: none;
}

.title {
  font-size: 1.6em;
  margin: 0;
}

.instructor {
  margin: 0;
}

.line {
  border-top: 1px solid #979797;
}

.buttons {
  position: absolute;
  bottom: 0.5em;
  left: 16px;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
}

.dates {
  position: absolute;
  bottom: 0.25em;
  right: -2.8em;
  height: 3.2em;
  width: 12.6em;
  padding: 0 0.5em;
  /* padding-left: 36px; */

  color: white;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.discovery .dates {
  background-image: linear-gradient(180deg, #28cede 0%, #56b4ff 97%);
  border-radius: 1.5em;
}

.dates > p {
  white-space: pre;
}

/** Larger devices and Landscape mobile */
@media only screen and (min-width: 601px) {
  .card-content {
    top: 23px;
    left: 36px;
    width: 298px;
  }

  .description {
    display: block;
  }

  .buttons {
    bottom: 1.5em;
    left: 36px;
    width: 20em;
  }

  .circle-date {
    bottom: -21%;
    right: -3%;
    width: 33%;
  }

  .dates {
    bottom: 1em;
    right: -1.6em;
    height: 3.2em;
    width: 12.6em;
    padding: 0 0.5em;
  }
}
</style>
